import { useEffect, useState } from 'react';
import { X, Link2, ArrowRightLeft } from 'lucide-react';
import { useT } from '../i18n';
import { useRole } from '../context/RoleContext';
import { useAuth } from '../context/AuthContext';
import { ContactTaskCreator } from './ContactTaskCreator';
import { RemindTaskAssignee } from './RemindTaskAssignee';
import { LineTextarea } from './LineTextarea';
import { TaskPrivacyField } from './TaskPrivacyField';
import { api } from '../api/client';
import { BareTextarea, getMilestoneOptions, getMilestoneDueMap, clampDueToMilestone } from './ProjectForm';
import { DropdownSelect } from './DropdownSelect';
import { DateHistory, formatPlannedSlot } from './DateHistory';
import { TaskTodoList } from '../proposals/vue-jalons-taches/TaskTodoList';
import { MultiSelectFilter } from './MultiSelectFilter';
import { projectDisplayCode } from '../utils/identifiers';
import type { PlannerTask, KanbanColumnKey, TaskDetail, Project, DateChange } from '../types';
import { TaskCollaboratorsField, ProjectPeopleHint, projectPeople } from '../proposals/collaborateurs-tache/TaskCollaboratorsField';
import { DocumentsList } from '../proposals/documents/DocumentsList';
import { NoMilestonesNotice, MissingMilestoneNotice, taskSaveErrorMessage } from './TaskMilestoneNotice';
import { MigrateTaskModal, canMigrateTask } from './MigrateTaskModal';

// The four fixed standalone-board lanes — matches KanbanBoardsPage's COLUMN_DEFS.
const COLUMN_KEYS: KanbanColumnKey[] = ['backlog', 'todo', 'in_progress', 'done', 'archived'];

function isColumnKey(v: string | null | undefined): v is KanbanColumnKey {
  return !!v && COLUMN_KEYS.some(k => k === v);
}

// Build a planned-slot ISO from a YYYY-MM-DD target + HH:MM time (both local),
// defaulting to 09:00 when no time is given. The planner stores a full timestamp
// (not just a date), so the date + time inputs are combined and widened to ISO.
function plannedISOFor(targetYMD: string, hhmm: string): string {
  const [y, m, d] = targetYMD.split('-').map(Number);
  const [hh, mm] = hhmm ? hhmm.split(':').map(Number) : [9, 0];
  const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
  return dt.toISOString();
}

// Local HH:MM of an existing planned timestamp (seeds the time input), or '' when
// nothing is planned yet.
function timeOfISO(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Field editor for a planner item, opened when a user clicks a project task or a
// Kanban card in the work-week planner. Edits fields in place through the
// existing task / kanban-card APIs, then calls onSaved so the parent reloads.
// Read-only context (project code or board name) sits in the header; nothing is
// deleted here — this is purely a field editor.
export function TaskDetailModal({ task, contextProjectId, projects, onClose, onSaved }: {
  task: PlannerTask;
  /** The project page this modal was opened from — lets the "linked project"
   *  indicator show whichever of the task's two projects isn't this one. */
  contextProjectId?: number;
  /** All projects, for the "linked projects" picker — omit to keep that field
   *  read-only (still shown in the header indicator either way). */
  projects?: Project[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useT();
  const { isAdmin, canSeeProposals } = useRole();
  const COLUMN_LABELS: Record<KanbanColumnKey, string> = {
    backlog: t('taskDetail.colBacklog'),
    todo: t('taskDetail.colTodo'),
    in_progress: t('taskDetail.colInProgress'),
    done: t('taskDetail.colDone'),
    archived: t('taskDetail.colArchived'),
  };
  const isProject = task.source === 'project';

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [assignee, setAssignee] = useState(task.assignee_name ?? '');
  // [Proposition: collaborateurs-tache] Collaborators and the project's people to pick them from.
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [people, setPeople] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState(task.due_date ? task.due_date.slice(0, 10) : '');
  const [plannedDate, setPlannedDate] = useState(task.planned_at ? task.planned_at.slice(0, 10) : '');
  // No time field any more (only the date is shown): the stored time is kept on save.
  const [plannedTime, setPlannedTime] = useState(timeOfISO(task.planned_at));
  const [dueDateHistory, setDueDateHistory] = useState<DateChange[]>([]);
  const [plannedAtHistory, setPlannedAtHistory] = useState<DateChange[]>([]);
  const [columnKey, setColumnKey] = useState<KanbanColumnKey>(
    isColumnKey(task.column_key) ? task.column_key : 'todo',
  );
  const [milestoneId, setMilestoneId] = useState('');
  const [milestoneOptions, setMilestoneOptions] = useState<{ value: string; label: string }[]>([]);
  const [milestoneDueMap, setMilestoneDueMap] = useState<Map<string, string>>(new Map());
  const [saving, setSaving] = useState(false);
  // "Migrer vers un autre projet" — from the task's home project, when the caller gave the projects to pick from.
  const [showMigrate, setShowMigrate] = useState(false);
  // Task author, for "Contact the creator" (filled from the full task record).
  const [creator, setCreator] = useState<{ id: number; name: string | null } | null>(null);
  // Public / private (set here, shown as a padlock on the card); null until the full record loads.
  const [privacy, setPrivacy] = useState<{ isPrivate: boolean; canManage: boolean } | null>(null);
  // "Envoyer un rappel" to the responsible (PMO / the project's PMO accompanist), from the full record.
  const [canRemind, setCanRemind] = useState(false);
  const { user } = useAuth();

  // Authoritative project identity — the seeded PlannerTask always carries
  // the page's own project, which is wrong for a task reached here via one
  // of its *linked* projects. homeProject is always the true primary (used to
  // exclude it from the linked-projects picker); currentProject/otherProjects
  // are context-relative for display, so the current project never also
  // shows up in "linked to" (and vice versa).
  const [homeProject, setHomeProject] = useState<{ id: number; name: string; code: string | null } | null>(null);
  // Editable only from its home (project_id) project — reached via a linked
  // project, it's a read-only copy so collaborators who don't share the same
  // team/context on the home project can't create conflicting edits. No
  // context (e.g. the personal planner) always counts as the home project.
  const isSource = homeProject == null || !contextProjectId || contextProjectId === homeProject.id;
  const [currentProject, setCurrentProject] = useState<{ id: number; name: string; code: string | null } | null>(null);
  const [otherProjects, setOtherProjects] = useState<{ id: number; name: string; code: string | null }[]>([]);
  const [linkedProjectIds, setLinkedProjectIds] = useState<number[]>([]);

  // Project tasks carry a richer record server-side (private notes, milestone,
  // etc.), so pull it on open and seed the editor from the authoritative
  // values. On failure we keep the planner item's fields (a graceful fallback).
  useEffect(() => {
    if (!isProject) return;
    let cancelled = false;
    (api.getTaskDetail(task.id) as Promise<TaskDetail>)
      .then((full) => {
        if (cancelled) return;
        setTitle(full.title);
        setDescription(full.description ?? '');
        setAssignee(full.assignee_name ?? '');
        setCollaborators(full.collaborator_names ?? []);
        setPeople(projectPeople(full));
        setNotes(full.private_notes ?? '');
        setDueDate(full.due_date ? full.due_date.slice(0, 10) : '');
        // Seed the planned slot from the saved task too: callers such as the
        // project page open this modal without it, and save() always rewrites
        // planned_at — without this, saving there wiped the planned date.
        setPlannedDate(full.planned_at ? full.planned_at.slice(0, 10) : '');
        setPlannedTime(timeOfISO(full.planned_at));
        setDueDateHistory(full.due_date_history ?? []);
        setPlannedAtHistory(full.planned_at_history ?? []);
        setMilestoneId(full.milestone_id ?? '');
        setMilestoneOptions(getMilestoneOptions(full.planning, t('common.dateFormatPlaceholder'), t('taskDetail.milestoneDuePrefix')));
        setMilestoneDueMap(getMilestoneDueMap(full.planning));
        setHomeProject({ id: full.project_id, name: full.project_name, code: full.project_code });
        setCreator({ id: full.created_by, name: full.created_by_name ?? null });
        setPrivacy({ isPrivate: !!full.is_private, canManage: !!full.can_manage_privacy });
        setCanRemind(!!full.can_remind);
        setLinkedProjectIds(full.linked_projects.map((lp) => lp.id));
        // Header shows whichever of this task's projects this modal was
        // opened from; "linked to" shows every other one.
        const allTaskProjects = [
          { id: full.project_id, name: full.project_name, code: full.project_code as string | null },
          ...full.linked_projects.map((lp) => ({ id: lp.id, name: lp.name, code: lp.project_code })),
        ];
        const effectiveContext = contextProjectId ?? full.project_id;
        const current = allTaskProjects.find((p) => p.id === effectiveContext) ?? allTaskProjects[0];
        setCurrentProject(current);
        setOtherProjects(allTaskProjects.filter((p) => p.id !== current.id));
      })
      .catch((err: Error & { code?: string }) => {
        // Private task this user can't open: say so and close instead of
        // showing an editor whose saves would all be refused.
        if (err.code === 'TASK_PRIVATE') { alert(t('tasks.privateLocked')); onClose(); }
        // Otherwise keep the planner-seeded values (graceful fallback).
      });
    return () => { cancelled = true; };
  }, [isProject, task.id, contextProjectId]);

  // The picker only shows/edits links *other than* whichever project this
  // modal was opened from — that one is implicit (it's why the task showed
  // up there), so editedIds excludes it and must be re-added before persisting.
  const changeLinkedProjects = (editedIds: number[]) => {
    const viewContext = currentProject?.id ?? homeProject?.id;
    const fullIds = viewContext != null && viewContext !== homeProject?.id ? [...editedIds, viewContext] : editedIds;
    setLinkedProjectIds(fullIds);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isProject) {
        await api.updateTask(task.id, {
          title,
          description,
          due_date: dueDate || null,
          assignee_name: assignee || null,
          // Only once the full record (and its collaborators) has loaded.
          ...(homeProject ? { collaborator_names: collaborators } : {}),
          private_notes: notes || null,
          milestone_id: milestoneId || null,
          linked_project_ids: linkedProjectIds,
        });
      } else {
        await api.kanbanUpdateCard(task.id, {
          title,
          description: description || null,
          assignee_name: assignee || null,
          column_key: columnKey,
        });
      }
      // Persist the planned slot through the planner API — a full timestamp built
      // from the date + time inputs. Applies to both project tasks and kanban
      // cards, and is the only writer of planned_at for either source.
      const plannedISO = plannedDate ? plannedISOFor(plannedDate, plannedTime) : null;
      await api.plannerSetPlan(task.source, task.id, plannedISO, task.planned_minutes || 60);
      onSaved();
    } catch (err: any) {
      alert(taskSaveErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div>
            {isProject ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>{currentProject?.name ?? task.project_title}</span>
                  {(currentProject?.code ?? task.project_code) && (
                    <span className="text-sm text-ink-3">{` - ${(currentProject?.code ?? task.project_code)}`}</span>
                  )}
                </div>
                {!isSource && (
                  <div className="text-sm text-ink-3" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }} title={t('tasks.readOnlyNotice')}>
                    <Link2 size={11} />
                    {t('tasks.primaryProjectPrefix')} {homeProject?.code || homeProject?.name}
                  </div>
                )}
                {isSource && otherProjects.length > 0 && (
                  <div className="text-sm text-ink-3" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                    <Link2 size={11} />
                    {t('tasks.presentInProjects', { projects: otherProjects.map((p) => p.code || p.name).join(', ') })}
                  </div>
                )}
              </>
            ) : (
              task.board_name && <span style={{ fontWeight: 700 }}>{task.board_name}</span>
            )}
          </div>
          <button className="close-btn" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body">
          {!isSource && <p className="pq-decision-help" style={{ marginTop: 0 }}>{t('tasks.readOnlyNotice')}</p>}
          <div className={isSource ? undefined : 'task-detail-readonly'}>
          <div className="form-group">
            <label className="form-label">{t('common.title')}</label>
            <LineTextarea
              style={{ fontWeight: 700 }}
              value={title}
              onChange={setTitle}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('common.description')}</label>
            <BareTextarea value={description} onChange={setDescription} placeholder={t('taskDetail.descriptionPlaceholder')} />
          </div>

          {/* [Proposition: vue-jalons-taches] To-do list (project tasks only) */}
          {isProject && (
            <div className="form-group">
              <TaskTodoList taskId={task.id} readOnly={!isSource} showHeader taskDueDate={dueDate || null} />
            </div>
          )}
          {/* [Proposition: documents] Files and links attached to the task. */}
          {isProject && (
            <div className="form-group">
              <DocumentsList target={{ type: 'task', id: task.id }} readOnly={!isSource} />
            </div>
          )}

          {isProject ? (
            <>
              <div className="form-group" style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label">{t('taskDetail.dueDate')}</label>
                  <input
                    className="form-input--bare"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    disabled={!isAdmin}
                    title={isAdmin ? undefined : t('taskDetail.dueDateAdminOnly')}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="form-label">{t('taskDetail.plannedDate')}</label>
                  <input
                    className="form-input--bare"
                    type="date"
                    value={plannedDate}
                    onChange={(e) => setPlannedDate(e.target.value)}
                  />
                </div>
              </div>
              {(dueDateHistory.length > 0 || plannedAtHistory.length > 0) && (
                <div className="form-group">
                  <DateHistory history={dueDateHistory} label={t('taskDetail.dueDate')} />
                  <DateHistory history={plannedAtHistory} label={t('taskDetail.plannedDate')} formatValue={formatPlannedSlot} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">{t('taskDetail.assignee')}</label>
                <input
                  className="form-input--bare"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder={t('taskDetail.unassigned')}
                />
              </div>
              {homeProject && (
                <div className="form-group">
                  <label className="form-label form-label--tip">{t('collab.label')} <ProjectPeopleHint projectId={homeProject.id} /></label>
                  <TaskCollaboratorsField
                    people={people}
                    assignee={assignee}
                    value={collaborators}
                    onChange={setCollaborators}
                    readOnly={!isSource}
                  />
                </div>
              )}
              {milestoneOptions.length > 0 && (
                <div className="form-group">
                  <label className="form-label">{t('taskDetail.contributesToMilestone')}</label>
                  <DropdownSelect
                    value={milestoneId}
                    options={milestoneOptions}
                    placeholder={t('tasks.chooseMilestone')}
                    onChange={(v) => {
                      const mid = String(v);
                      setMilestoneId(mid);
                      // Clamp to the (current) milestone's estimated end, same as
                      // the Tasks page — otherwise the due date silently keeps
                      // whatever it was before switching milestones.
                      setDueDate((d) => clampDueToMilestone(d, mid ? (milestoneDueMap.get(mid) || '') : ''));
                    }}
                  />
                  {!milestoneId && <MissingMilestoneNotice />}
                </div>
              )}
              {/* No milestone exists yet on this project: the task has none. */}
              {homeProject && milestoneOptions.length === 0 && <NoMilestonesNotice projectId={homeProject.id} existingTask />}
              {privacy && (
                <div className="form-group">
                  <label className="form-label">{t('tasks.privacyLabel')}</label>
                  <TaskPrivacyField taskId={task.id} isPrivate={privacy.isPrivate} canManage={isSource && privacy.canManage}
                    onChanged={(isPrivate) => setPrivacy({ ...privacy, isPrivate })} />
                </div>
              )}
              {projects && (
                <div className="form-group">
                  <label className="form-label">{t('tasks.linkedProject')}</label>
                  {currentProject && homeProject && currentProject.id !== homeProject.id && (
                    <span className="badge" style={{ marginBottom: 4 }}>
                      {t('tasks.primaryProjectPrefix')} {homeProject.code?.toUpperCase() || homeProject.name}
                    </span>
                  )}
                  <div className="linked-projects-field">
                    <MultiSelectFilter
                      label=""
                      options={projects
                        .filter(p => p.id !== homeProject?.id && p.id !== currentProject?.id)
                        .map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` }))}
                      selected={linkedProjectIds.filter((id) => id !== currentProject?.id)}
                      onChange={changeLinkedProjects}
                      placeholder={t('tasks.linkedProjectPlaceholder')}
                    />
                  </div>
                </div>
              )}
              {/* [Proposition: vue-jalons-taches] For the proposals' reviewers the private notes are
                  gone (notes go in the to-do items); an existing note stays readable. */}
              {!canSeeProposals ? (
                <div className="form-group">
                  <label className="form-label">{t('taskDetail.privateNotes')}</label>
                  <BareTextarea value={notes} onChange={setNotes} placeholder={t('taskDetail.privateNotesPlaceholder')} />
                </div>
              ) : notes.trim() ? (
                <div className="form-group">
                  <label className="form-label">{t('taskDetail.privateNotes')}</label>
                  <div className="legacy-notes">{notes}</div>
                  <div className="docs-hint">{t('vjt.legacyNotesHint')}</div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">{t('taskDetail.assignee')}</label>
                <input
                  className="form-input--bare"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder={t('taskDetail.unassigned')}
                />
              </div>
              <div className="form-group" style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label">{t('taskDetail.plannedDate')}</label>
                  <input
                    className="form-input--bare"
                    type="date"
                    value={plannedDate}
                    onChange={(e) => setPlannedDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('taskDetail.column')}</label>
                <div className="selectable-group">
                  {COLUMN_KEYS.map(key => (
                    <button
                      key={key}
                      type="button"
                      className={`selectable${columnKey === key ? ' active' : ''}`}
                      onClick={() => setColumnKey(key)}
                    >
                      {COLUMN_LABELS[key]}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          </div>
        </div>

        <div className="modal-footer task-detail-modal-footer">
          {isProject && (
            <ContactTaskCreator taskId={task.id} creatorId={creator?.id} creatorName={creator?.name} currentUserId={user?.id} />
          )}
          {isProject && (
            <RemindTaskAssignee taskId={task.id} canRemind={canRemind} assigneeName={assignee} />
          )}
          {/* Only the PMO (admins) and the project's PMO accompanist move a task to another project. */}
          {isProject && isSource && homeProject && projects && canMigrateTask(isAdmin, user?.name, projects.find(p => p.id === homeProject.id)?.ownership) && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowMigrate(true)}>
              <ArrowRightLeft size={14} /> {t('tasks.migrateTask')}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          {isSource && (
            <button className="btn btn-accent" disabled={saving || !title.trim()} onClick={save}>
              {t('common.save')}
            </button>
          )}
        </div>
      </div>
      {showMigrate && homeProject && projects && (
        <MigrateTaskModal
          taskId={task.id}
          fromProjectId={homeProject.id}
          projects={projects}
          onClose={() => setShowMigrate(false)}
          onMigrated={() => { setShowMigrate(false); onSaved(); }}
        />
      )}
    </div>
  );
}
